import { Capacitor, registerPlugin } from '@capacitor/core';
import { OAuthProvider, signInWithCredential, signInWithPopup, updateProfile, reauthenticateWithCredential, reauthenticateWithPopup, User } from 'firebase/auth';
import { auth } from '../firebase/config';
import { isIOS } from '../utils/platform';

const AppleSignIn = registerPlugin<{
  signIn(): Promise<{ identityToken: string; nonce: string; name?: string; authorizationCode?: string }>;
}>('AppleSignIn');

// Used only after an explicit deletion confirmation. Reauthentication preserves
// the current account and rejects selecting a different Apple account.
// Credentials stay in memory for the server's revocation step, never storage.
export async function reauthenticateAppleForDeletion(user: User) {
  const provider = new OAuthProvider('apple.com');
  if (Capacitor.getPlatform() === 'android') {
    const { androidAppleCredential } = await import('./androidAuth');
    const response = await androidAppleCredential();
    if (!response.accessToken) throw new Error('Apple did not return authorization for account deletion. Please try again.');
    const result = await reauthenticateWithCredential(user, provider.credential({ idToken: response.idToken, rawNonce: response.nonce }));
    return { tokenType: 'ACCESS_TOKEN' as const, token: response.accessToken, idToken: await result.user.getIdToken(true) };
  }
  if (isIOS()) {
    const response = await AppleSignIn.signIn();
    if (!response.authorizationCode) {
      throw new Error('Please update FinFlow before deleting an Apple sign-in account.');
    }
    const credential = provider.credential({ idToken: response.identityToken, rawNonce: response.nonce });
    const result = await reauthenticateWithCredential(user, credential);
    return { tokenType: 'CODE' as const, token: response.authorizationCode, idToken: await result.user.getIdToken(true) };
  }
  const result = await reauthenticateWithPopup(user, provider);
  const credential = OAuthProvider.credentialFromResult(result);
  if (!credential?.accessToken) throw new Error('Apple did not return authorization for account deletion. Please try again.');
  return { tokenType: 'ACCESS_TOKEN' as const, token: credential.accessToken, idToken: await result.user.getIdToken(true) };
}

export async function signInWithApple() {
  const provider = new OAuthProvider('apple.com');
  provider.addScope('email');
  provider.addScope('name');
  if (Capacitor.getPlatform() === 'android') {
    const { androidAppleCredential } = await import('./androidAuth');
    const response = await androidAppleCredential();
    const result = await signInWithCredential(auth, provider.credential({ idToken: response.idToken, rawNonce: response.nonce }));
    if (response.name && !result.user.displayName) await updateProfile(result.user, { displayName: response.name });
    return result;
  }
  if (!isIOS()) return signInWithPopup(auth, provider);

  // Apple receives the hashed nonce; Firebase validates the original nonce.
  const response = await AppleSignIn.signIn();
  const credential = provider.credential({ idToken: response.identityToken, rawNonce: response.nonce });
  const result = await signInWithCredential(auth, credential);
  // Apple only supplies a name on the first authorization.
  if (response.name && !result.user.displayName) {
    await updateProfile(result.user, { displayName: response.name });
  }
  return result;
}
